import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useRouter } from '../../router/Router';
import { SEED_UNIVERSITIES } from '../../constants/mockSeed';
import { demandRequestService } from '../../services/demandRequestService';
import { studentDemandService } from '../../services/studentDemandService';
import { DemandRequest, TripType } from '../../types';
import {
  Send,
  Calendar,
  Clock,
  MapPin,
  Building2,
  CheckCircle2,
  AlertCircle,
  Users,
  Repeat,
  ArrowRight,
  Info,
  Sparkles,
  FileText,
  Clock4,
  XCircle,
} from 'lucide-react';
import './DemandRequestForm.css';

const ALEXANDRIA_PICKUP_POINTS = [
  'الموقف الجديد بمحرم بيك',
  'الكيلو 21',
];

export const DemandRequestForm: React.FC = () => {
  const { user, studentProfile } = useAuth();
  const { navigate } = useRouter();

  // Active Tab: New Request vs My Requests
  const [activeTab, setActiveTab] = useState<'NEW' | 'MY_REQUESTS'>('NEW');

  // Form State
  const [selectedUniId, setSelectedUniId] = useState(studentProfile?.universityId || 'tanta');
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toISOString().split('T')[0];
  });
  const [tripType, setTripType] = useState<TripType>('ROUND_TRIP');
  const [departureTime, setDepartureTime] = useState('06:30');
  const [arrivalTime, setArrivalTime] = useState('08:30');

  const defaultStation = studentProfile?.assignedRoute?.includes('21') || studentProfile?.defaultPickupStation?.includes('21')
    ? 'الكيلو 21'
    : 'الموقف الجديد بمحرم بيك';
  const [pickupLocation, setPickupLocation] = useState(defaultStation);
  const [notes, setNotes] = useState('');

  // Submission Status
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedRequest, setSubmittedRequest] = useState<DemandRequest | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Student's Requests
  const [myRequests, setMyRequests] = useState<DemandRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  // Load student requests
  const loadMyRequests = async () => {
    if (!user?.uid) return;
    setLoadingRequests(true);
    try {
      const list = await demandRequestService.getStudentDemandRequests(user.uid);
      setMyRequests(list);
    } catch (err) {
      console.warn('Error loading student demand requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    loadMyRequests();
  }, [user?.uid]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.uid) return;

    setErrorMessage(null);
    setIsSubmitting(true);

    const selectedUni = SEED_UNIVERSITIES.find((u) => u.id === selectedUniId) || {
      id: 'other',
      name: studentProfile?.universityName || 'جامعة أخرى',
      governorate: studentProfile?.universityGovernorate || 'أخرى',
    };

    const res = await demandRequestService.submitDemandRequest({
      studentId: user.uid,
      studentSnapshot: {
        fullName: studentProfile?.fullName || 'طالب مسجل',
        email: studentProfile?.email || user.email || '',
        phoneNumber: studentProfile?.phoneNumber || '',
      },
      universityId: selectedUni.id,
      universityName: selectedUni.name,
      governorate: selectedUni.governorate,
      date,
      preferredDepartureTime: departureTime,
      preferredArrivalTime: arrivalTime || undefined,
      tripType,
      pickupLocation,
      notes: notes ? `${notes} (محطة الركوب المطلوبة: ${pickupLocation})` : `محطة الركوب المطلوبة: ${pickupLocation}`,
    });

    // Also mirror to studentDemand collection to feed demand department records & KPIs
    try {
      await studentDemandService.createDemandRecord(
        {
          studentName: studentProfile?.fullName || 'طالب مسجل',
          phone: studentProfile?.phoneNumber || '01000000000',
          areaId: 'alex_east_smouha',
          areaName: pickupLocation,
          universityId: selectedUni.id,
          universityName: selectedUni.name,
          universityGovernorate: selectedUni.governorate,
          preferredDepartureTime: departureTime,
          preferredReturnTime: arrivalTime || undefined,
          notes: notes ? `${notes} (محطة الركوب: ${pickupLocation})` : `محطة الركوب: ${pickupLocation}`,
        },
        {
          uid: user.uid,
          email: studentProfile?.email || user.email || 'student@nashgo.com',
          name: studentProfile?.fullName || 'طالب مسجل',
        },
        { skipDuplicateCheck: true }
      );
    } catch (dmdErr) {
      console.warn('Could not mirror demand request to studentDemand:', dmdErr);
    }

    setIsSubmitting(false);

    if (res.success && res.data) {
      setSubmittedRequest(res.data);
      loadMyRequests();
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setErrorMessage(res.error || 'تعذر إرسال الطلب، يرجى المحاولة مرة أخرى.');
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    if (!window.confirm('هل أنت متأكد من رغبتك في إلغاء هذا الطلب؟')) return;
    const ok = await demandRequestService.cancelDemandRequest(requestId);
    if (ok) {
      loadMyRequests();
    }
  };

  return (
    <div className="demand-request-page" dir="rtl">
      {/* Header Banner */}
      <div className="demand-hero-card">
        <div className="demand-hero-content">
          <div className="demand-hero-badge">
            <Sparkles size={15} />
            <span>نظام قياس وتجميع الطلب التشغيلي</span>
          </div>
          <h2>لم تجد الموعد أو خط السير المناسب لجامعتك؟</h2>
          <p>
            أرسل موعدك المطلوب! نقوم بتجميع رغبات الطلاب المتشابهة لتوفير حافلة سياحية مجهزة (50 راكباً) خاصة بكم تنطلق من الإسكندرية مباشرة لبوابة كليتك.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="demand-tabs-nav">
        <button
          type="button"
          className={`demand-tab-btn ${activeTab === 'NEW' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('NEW');
            setSubmittedRequest(null);
          }}
        >
          <Send size={16} />
          <span>طلب موعد جديد</span>
        </button>

        <button
          type="button"
          className={`demand-tab-btn ${activeTab === 'MY_REQUESTS' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('MY_REQUESTS');
            loadMyRequests();
          }}
        >
          <FileText size={16} />
          <span>طلباتي السابقة ({myRequests.length})</span>
        </button>
      </div>

      {/* TAB 1: NEW REQUEST */}
      {activeTab === 'NEW' && (
        <>
          {submittedRequest ? (
            <div className="demand-success-card">
              <div className="success-icon-bubble">
                <CheckCircle2 size={40} className="text-emerald-500" />
              </div>
              <h3>تم تسجيل طلبك بنجاح!</h3>
              <p className="success-desc">
                كود الطلب المرجعي الخاص بك هو:{' '}
                <strong className="font-mono text-primary">{submittedRequest.requestCode}</strong>
              </p>

              <div className="demand-summary-box">
                <div className="summary-row">
                  <span className="summary-label">الجامعة المطلوبة:</span>
                  <strong className="summary-val">{submittedRequest.universityName}</strong>
                </div>
                <div className="summary-row">
                  <span className="summary-label">تاريخ السفر:</span>
                  <span className="summary-val font-mono">{submittedRequest.date}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">نوع الرحلة:</span>
                  <span className="summary-val">
                    {submittedRequest.tripType === 'ROUND_TRIP' ? 'ذهاب وعودة' : 'ذهاب فقط'}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">وقت التحرك المفضل:</span>
                  <span className="summary-val font-mono">{submittedRequest.preferredDepartureTime}</span>
                </div>
              </div>

              <div className="demand-info-note">
                <Info size={18} />
                <span>
                  فور وصول عدد الطلاب المسجلين لنفس الخط والموعد إلى الحد التشغيلي (8 إلى 14 طالباً)، سيتم إخطارك فورا وتفعيل الحجز المباشر.
                </span>
              </div>

              <div className="success-actions-row">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => {
                    setSubmittedRequest(null);
                    setNotes('');
                  }}
                >
                  تقديم طلب موعد آخر
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setActiveTab('MY_REQUESTS')}
                >
                  استعراض طلباتي
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="demand-form-card">
              {errorMessage && (
                <div className="demand-error-banner">
                  <AlertCircle size={18} />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* 1. University Selection */}
              <div className="form-section">
                <label className="section-label">
                  <Building2 size={18} className="text-primary" />
                  <span>1. اختر الجامعة والكلية المستهدفة</span>
                </label>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="field-label">اسم الجامعة *</label>
                    <select
                      className="form-control"
                      value={selectedUniId}
                      onChange={(e) => setSelectedUniId(e.target.value)}
                      required
                    >
                      {SEED_UNIVERSITIES.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} — {u.governorate} {u.campus ? `(${u.campus})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="field-label">نوع خط السير *</label>
                    <div className="trip-type-options">
                      <button
                        type="button"
                        className={`type-btn ${tripType === 'ROUND_TRIP' ? 'selected' : ''}`}
                        onClick={() => setTripType('ROUND_TRIP')}
                      >
                        <Repeat size={16} />
                        <span>ذهاب وعودة (اليوم الكامل)</span>
                      </button>
                      <button
                        type="button"
                        className={`type-btn ${tripType === 'ONE_WAY' ? 'selected' : ''}`}
                        onClick={() => setTripType('ONE_WAY')}
                      >
                        <ArrowRight size={16} />
                        <span>ذهاب فقط للجامعة</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Date & Time Selection */}
              <div className="form-section">
                <label className="section-label">
                  <Calendar size={18} className="text-primary" />
                  <span>2. تاريخ ومواعيد السفر المطلوبة</span>
                </label>
                <div className="form-grid-3">
                  <div className="form-group">
                    <label className="field-label">تاريخ السفر *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={date}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="field-label">وقت التحرك المفضل من الإسكندرية *</label>
                    <input
                      type="time"
                      className="form-control font-mono"
                      value={departureTime}
                      onChange={(e) => setDepartureTime(e.target.value)}
                      required
                    />
                    <div className="quick-time-chips">
                      {['06:00', '06:30', '07:00'].map((t) => (
                        <button
                          key={t}
                          type="button"
                          className={`time-chip ${departureTime === t ? 'active' : ''}`}
                          onClick={() => setDepartureTime(t)}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  {tripType === 'ROUND_TRIP' && (
                    <div className="form-group">
                      <label className="field-label">وقت العودة المفضل من الجامعة</label>
                      <input
                        type="time"
                        className="form-control font-mono"
                        value={arrivalTime}
                        onChange={(e) => setArrivalTime(e.target.value)}
                      />
                      <div className="quick-time-chips">
                        {['14:30', '15:30', '16:30'].map((t) => (
                          <button
                            key={t}
                            type="button"
                            className={`time-chip ${arrivalTime === t ? 'active' : ''}`}
                            onClick={() => setArrivalTime(t)}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 3. Alexandria Pickup Location & Notes */}
              <div className="form-section">
                <label className="section-label">
                  <MapPin size={18} className="text-primary" />
                  <span>3. محطة التجمع بالإسكندرية والملاحظات</span>
                </label>

                <div className="form-group">
                  <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                    <label className="field-label mb-0">محطة الانطلاق المعتمدة بالإسكندرية *</label>
                    <span className="text-xs text-slate-500 font-bold">
                      محطات الانطلاق: الموقف الجديد بمحرم بيك أو الكيلو 21
                    </span>
                  </div>
                  {studentProfile?.assignedRoute && (
                    <div className="mb-2 p-2 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800 flex items-center gap-1.5 font-bold">
                      <span>خط السير المعين لك في ملفك الطلابي:</span>
                      <span className="underline">{studentProfile.assignedRoute}</span>
                    </div>
                  )}
                  <select
                    className="form-control"
                    value={pickupLocation}
                    onChange={(e) => setPickupLocation(e.target.value)}
                    required
                  >
                    {ALEXANDRIA_PICKUP_POINTS.map((pt) => (
                      <option key={pt} value={pt}>
                        {pt}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="field-label">
                    ملاحظات إضافية (مواعيد الامتحانات، أسماء الزملاء الراغبين في السفر معك، كليتك بالتحديد)
                  </label>
                  <textarea
                    className="form-control textarea-field"
                    rows={3}
                    placeholder="مثال: عندي امتحان ميدتيرم عملي الساعة 9:00 ص، ومعايا 3 زملاء حابين نركب مع بعض..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>

              {/* Student Information Snapshot */}
              <div className="student-snapshot-box">
                <div className="snapshot-header">
                  <Users size={16} />
                  <span>بيانات الاتصال المعتمدة في طلبك:</span>
                </div>
                <div className="snapshot-grid">
                  <div>
                    <span className="snap-label">الطالب:</span>
                    <strong className="snap-val">{studentProfile?.fullName || 'طالب مسجل'}</strong>
                  </div>
                  <div>
                    <span className="snap-label">الهاتف:</span>
                    <span className="snap-val font-mono" dir="ltr">
                      {studentProfile?.phoneNumber || 'غير مسجل'}
                    </span>
                  </div>
                  <div>
                    <span className="snap-label">محل الإقامة:</span>
                    <span className="snap-val">{studentProfile?.address || 'الإسكندرية'}</span>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="form-footer-actions">
                <button
                  type="submit"
                  className="btn-primary btn-submit-demand"
                  disabled={isSubmitting}
                >
                  <Send size={18} />
                  <span>{isSubmitting ? 'جارٍ تسجيل الطلب...' : 'إرسال طلب الرحلة الآن'}</span>
                </button>
              </div>
            </form>
          )}
        </>
      )}

      {/* TAB 2: MY REQUESTS */}
      {activeTab === 'MY_REQUESTS' && (
        <div className="my-demand-requests-view">
          {loadingRequests ? (
            <div className="demand-loading-state">
              <div className="loading-spinner"></div>
              <span>جارٍ تحميل طلباتك المسجلة...</span>
            </div>
          ) : myRequests.length === 0 ? (
            <div className="demand-empty-state">
              <Clock4 size={48} className="empty-icon" />
              <h3>لا توجد لديك طلبات رحلات حالياً</h3>
              <p>إذا لم تجد موعداً مناسباً لرحلات كليتك في جدول الرحلات، يمكنك طلب موعد مخصص بكل سهولة.</p>
              <button
                type="button"
                className="btn-primary"
                onClick={() => setActiveTab('NEW')}
              >
                تقديم طلب رحلة جديدة
              </button>
            </div>
          ) : (
            <div className="demand-cards-grid">
              {myRequests.map((req) => {
                const isPending = req.status === 'PENDING';
                const isCreated = req.status === 'TRIP_CREATED';
                const isRejected = req.status === 'REJECTED';

                return (
                  <div key={req.id} className="demand-req-card">
                    <div className="req-card-header">
                      <div>
                        <span className="req-code-badge">{req.requestCode}</span>
                        <h4 className="req-uni-title">{req.universityName}</h4>
                      </div>
                      <span
                        className={`req-status-pill ${
                          isCreated ? 'confirmed' : isRejected ? 'cancelled' : 'pending'
                        }`}
                      >
                        {isCreated
                          ? '🎉 تم جدولة الرحلة'
                          : isRejected
                          ? 'تعذر توفير الموعد'
                          : req.status === 'REVIEWED'
                          ? 'قيد الترتيب والجدولة'
                          : 'بانتظار اكتمال العدد'}
                      </span>
                    </div>

                    <div className="req-card-body">
                      <div className="req-info-line">
                        <Calendar size={14} className="text-slate-400" />
                        <span>تاريخ السفر:</span>
                        <strong className="font-mono">{req.date}</strong>
                      </div>
                      <div className="req-info-line">
                        <Clock size={14} className="text-slate-400" />
                        <span>موعد التحرك:</span>
                        <strong className="font-mono">{req.preferredDepartureTime}</strong>
                        {req.tripType === 'ROUND_TRIP' && (
                          <span className="trip-dir-tag">ذهاب وعودة</span>
                        )}
                      </div>
                      {req.notes && (
                        <div className="req-notes-block">
                          <span>{req.notes}</span>
                        </div>
                      )}
                    </div>

                    <div className="req-card-footer">
                      <span className="req-created-at text-xs text-slate-400">
                        تاريخ الطلب: {req.createdAt ? req.createdAt.split('T')[0] : '—'}
                      </span>

                      {isCreated && req.matchedTripId && (
                        <button
                          type="button"
                          className="btn-primary btn-sm"
                          onClick={() => navigate(`/student/trips/${req.matchedTripId}/seats`)}
                        >
                          <span>احجز مقعدك الآن</span>
                          <ArrowRight size={14} />
                        </button>
                      )}

                      {isPending && (
                        <button
                          type="button"
                          className="btn-cancel-req"
                          onClick={() => handleCancelRequest(req.id)}
                        >
                          <XCircle size={14} />
                          <span>إلغاء الطلب</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
